/**
 * PageHeader — shared title/subtitle/actions strip for the 5 primary tabs.
 *
 * Introduced in v0.5.4 (AE-069) so Today / Portfolio / Research / Approvals
 * / Settings all use the same hierarchy:
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ <title — text-lg font-bold>                  [actions...]  │
 *   │ <subtitle — text-[11px] text-ink-mute>                     │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Before this, page hero patterns diverged (h2 text-lg on Portfolio vs
 * h2 text-sm on Approvals vs accent-bordered card on Research vs no
 * title at all on Today/Settings). The lack of a shared component made
 * cross-tab navigation feel like five different products.
 */
import type { ReactNode } from 'react';

export const PAGE_MAX_W = 'max-w-4xl';
export const PAGE_MAX_W_NARROW = 'max-w-2xl';

export function PageHeader({
  title,
  subtitle,
  actions,
  badge,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Optional pill rendered next to the title (e.g. "Engineer · SIM only"). */
  badge?: ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-bold">{title}</h2>
            {badge}
          </div>
          {subtitle && (
            <div className="text-[11px] text-ink-mute mt-0.5">{subtitle}</div>
          )}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}
